import { useCallback, useEffect, useMemo } from "react";
import { formatUserName, Person } from "@forgepoint/shared";
import { usePeopleStore } from "@/modules/people/peopleStore";
import { EntitySelector } from "@/components/EntitySelector";

type PeopleSelectorBaseProps = {
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  size?: "default" | "sm";
};

/**
 * Props for the single-select variant of PeopleSelector.
 */
export type SinglePeopleSelectorProps = PeopleSelectorBaseProps & {
  mode?: "single";
  currentlySelectedId: string | null;
  onSelectId: (personId: string | null) => void;
};

/**
 * Props for the multi-select variant of PeopleSelector.
 */
export type MultiPeopleSelectorProps = PeopleSelectorBaseProps & {
  mode: "multi";
  selectedPersonIds: string[];
  onPersonIdsChange: (personIds: string[]) => void;
};

/**
 * Discriminated props for PeopleSelector, selecting between single and multi modes.
 */
export type PeopleSelectorProps =
  | SinglePeopleSelectorProps
  | MultiPeopleSelectorProps;

/**
 * Provides the shared data and callbacks the PeopleSelector needs from the people store.
 * @returns The people map, loading state, and the fetch/display helpers for the selector
 */
function usePeopleSelectorData() {
  const { people, listLoading, fetchPeople } = usePeopleStore();

  const peopleMap = useMemo(() => new Map(people), [people]);

  const fetchEntities = useCallback(
    async (search?: string) => {
      const result = await fetchPeople(search ? { search } : undefined);
      return result.ids;
    },
    [fetchPeople],
  );

  const getDisplayName = useCallback(
    (person: Person) =>
      formatUserName(
        person.firstName,
        person.lastName,
        person.email || "Unknown",
      ),
    [],
  );

  const getSecondaryInfo = useCallback(
    (person: Person) => person.email ?? "",
    [],
  );

  return {
    peopleMap,
    listLoading,
    fetchPeople,
    fetchEntities,
    getDisplayName,
    getSecondaryInfo,
  };
}

/**
 * A selector for choosing people, backed by the people store.
 * In single mode it selects one person; in multi mode it manages a set of person IDs
 * rendered as removable badges. Display name is "firstName lastName", falling back to email.
 * @param props - Single or multi selector props discriminated by `mode`
 * @returns The rendered entity selector for people
 */
export function PeopleSelector(props: PeopleSelectorProps) {
  const {
    placeholder = "Select a person...",
    className,
    disabled = false,
    size = "default",
  } = props;

  const {
    peopleMap,
    listLoading,
    fetchPeople,
    fetchEntities,
    getDisplayName,
    getSecondaryInfo,
  } = usePeopleSelectorData();

  const hasMissingPeople =
    props.mode === "multi"
      ? props.selectedPersonIds.some((id) => !peopleMap.has(id))
      : props.currentlySelectedId !== null &&
        !peopleMap.has(props.currentlySelectedId);

  useEffect(() => {
    if (hasMissingPeople) {
      fetchPeople();
    }
  }, [hasMissingPeople, fetchPeople]);

  const sharedProps = {
    entities: peopleMap,
    listLoading,
    fetchEntities,
    getDisplayName,
    getSecondaryInfo,
    placeholder,
    searchPlaceholder: "Search people...",
    className,
    disabled,
    size,
    entityLabel: "people",
  } as const;

  const modeProps =
    props.mode === "multi"
      ? ({
          mode: "multi",
          selectedIds: props.selectedPersonIds,
          onSelectedIdsChange: props.onPersonIdsChange,
        } as const)
      : ({
          currentlySelectedId: props.currentlySelectedId,
          onSelectId: props.onSelectId,
        } as const);

  return <EntitySelector<Person> {...sharedProps} {...modeProps} />;
}
